import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  Container,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { I18n, setupI18n } from "@lingui/core";
import { I18nProvider } from "@lingui/react";
import { tallyTokens } from "../theme.tsx";
import { Trans, useLingui } from "@lingui/react/macro";
import TallyBrownLogo from "../assets/tally-brown.png";
import {
  CHANGELOG_CATEGORIES,
  CHANGELOG_ENTRIES,
  ChangelogCategory,
  ChangelogEntry,
} from "../content/changelog";
import { CHANGELOG_ENTRIES_DA } from "../content/changelog.da";

interface ChangelogProps {
  /** Overridable for tests; defaults to the checked-in content file. */
  entries?: ChangelogEntry[];
  /** Overridable for tests; defaults to the checked-in Danish translations. */
  translations?: ChangelogEntry[];
}

type ChangelogLanguage = "da" | "en";

/** Language names are shown in their own language, so they are not translated. */
const LANGUAGES: { code: ChangelogLanguage; label: string }[] = [
  { code: "da", label: "Dansk" },
  { code: "en", label: "English" },
];
const DEFAULT_LANGUAGE: ChangelogLanguage = "da";
const STORAGE_KEY = "changelog-language";

const readStoredLanguage = (): ChangelogLanguage => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "da" || stored === "en") {
      return stored;
    }
  } catch {
    // Storage can be unavailable (e.g. private mode); fall back to the default.
  }
  return DEFAULT_LANGUAGE;
};

const storeLanguage = (language: ChangelogLanguage) => {
  try {
    localStorage.setItem(STORAGE_KEY, language);
  } catch {
    // Not persisting the choice is harmless.
  }
};

/** Danish text where a translation exists for the entry's date, else English. */
const withTranslations = (
  entries: ChangelogEntry[],
  translations: ChangelogEntry[],
  language: ChangelogLanguage,
): ChangelogEntry[] => {
  if (language === "en") {
    return entries;
  }
  const byDate = new Map(translations.map((entry) => [entry.date, entry]));
  return entries.map((entry) => byDate.get(entry.date) ?? entry);
};

const loadMessages = async (language: ChangelogLanguage) => {
  const { messages } = await import(`../locales/${language}/messages.po`);
  return messages;
};

interface MonthGroup {
  key: string; // YYYY-MM
  entries: ChangelogEntry[];
}

const parseDate = (date: string) => new Date(`${date}T00:00:00Z`);

/** Newest first, grouped by calendar month (UTC, matching the stored dates). */
const groupByMonth = (entries: ChangelogEntry[]): MonthGroup[] => {
  const sorted = [...entries].sort((a, b) => b.date.localeCompare(a.date));
  const groups: MonthGroup[] = [];
  for (const entry of sorted) {
    const key = entry.date.slice(0, 7);
    const last = groups[groups.length - 1];
    if (last && last.key === key) {
      last.entries.push(entry);
    } else {
      groups.push({ key, entries: [entry] });
    }
  }
  return groups;
};

interface ChangelogPageProps {
  entries: ChangelogEntry[];
  translations: ChangelogEntry[];
  language: ChangelogLanguage;
  onLanguageChange: (language: ChangelogLanguage) => void;
}

const ChangelogPage: React.FC<ChangelogPageProps> = ({
  entries: englishEntries,
  translations,
  language,
  onLanguageChange,
}) => {
  const theme = useTheme();
  const { t, i18n } = useLingui();
  const entries = useMemo(
    () => withTranslations(englishEntries, translations, language),
    [englishEntries, translations, language],
  );

  const categoryLabels: Record<ChangelogCategory, string> = {
    Added: t({ message: "Added", context: "Changelog category heading" }),
    Changed: t({ message: "Changed", context: "Changelog category heading" }),
    Fixed: t({ message: "Fixed", context: "Changelog category heading" }),
    Removed: t({ message: "Removed", context: "Changelog category heading" }),
    Deprecated: t({
      message: "Deprecated",
      context: "Changelog category heading",
    }),
    Security: t({ message: "Security", context: "Changelog category heading" }),
    Performance: t({
      message: "Performance",
      context: "Changelog category heading",
    }),
  };

  const groups = useMemo(() => groupByMonth(entries), [entries]);

  const formatDate = (date: string, options: Intl.DateTimeFormatOptions) =>
    new Intl.DateTimeFormat(i18n.locale || "en", {
      ...options,
      timeZone: "UTC",
    }).format(parseDate(date));

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: theme.palette.background.default,
        color: theme.palette.primary.main,
        py: { xs: 3, md: 4 },
        // #root sets text-align:center globally; entry text must opt out so
        // bullets and dates read as a left-aligned list, not centred prose.
        textAlign: "left",
      }}
    >
      <Container maxWidth="md">
        <Box textAlign="center" mb={{ xs: 4, md: 6 }}>
          <Box
            component="img"
            src={TallyBrownLogo}
            alt="Tally Logo"
            sx={{ height: { xs: "44px", sm: "56px" }, mb: 2 }}
          />
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: "2rem", sm: "2.75rem", md: "3.25rem" },
              fontWeight: "bold",
              color: theme.palette.primary.main,
              m: 0,
            }}
          >
            <Trans context="Changelog page title">What's new</Trans>
          </Typography>
          <Typography
            variant="h6"
            sx={{
              mt: 1,
              fontSize: { xs: "1rem", sm: "1.2rem" },
              color: tallyTokens.ink2,
            }}
          >
            <Trans context="Changelog page subtitle">
              The latest improvements to Tally.
            </Trans>
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={language}
            onChange={(_, next: ChangelogLanguage | null) => {
              // An exclusive group reports null when the active button is clicked
              // again; keep the current language instead of deselecting.
              if (next) {
                onLanguageChange(next);
              }
            }}
            aria-label={t({
              message: "Language",
              context: "Changelog language toggle label",
            })}
            sx={{ mt: 2 }}
          >
            {LANGUAGES.map(({ code, label }) => (
              <ToggleButton
                key={code}
                value={code}
                lang={code}
                sx={{ textTransform: "none", px: 2 }}
              >
                {label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>

        {groups.length === 0 ? (
          <Paper
            elevation={0}
            data-testid="changelog-empty"
            sx={{
              p: 4,
              textAlign: "center",
              backgroundColor: "transparent",
              border: `2px solid ${theme.palette.primary.main}`,
            }}
          >
            <Typography variant="body1" sx={{ color: tallyTokens.ink2 }}>
              <Trans context="Changelog empty state message">
                Nothing published yet — check back soon.
              </Trans>
            </Typography>
          </Paper>
        ) : (
          groups.map((group) => (
            <Box
              key={group.key}
              component="section"
              sx={{ mb: { xs: 4, md: 6 } }}
            >
              <Typography
                variant="h4"
                component="h2"
                sx={{
                  color: theme.palette.primary.main,
                  fontSize: { xs: "1.5rem", sm: "2rem" },
                  mb: 2,
                  pb: 1,
                  borderBottom: `1px solid ${theme.palette.primary.main}`,
                }}
              >
                {formatDate(`${group.key}-01`, {
                  month: "long",
                  year: "numeric",
                })}
              </Typography>
              {group.entries.map((entry) => (
                <Paper
                  key={entry.date}
                  elevation={0}
                  component="article"
                  sx={{
                    p: { xs: 2, sm: 3 },
                    mb: 2,
                    backgroundColor: "transparent",
                    border: `1px solid ${theme.palette.primary.main}`,
                    borderRadius: 2,
                  }}
                >
                  <Typography
                    variant="h6"
                    component="h3"
                    sx={{ color: tallyTokens.ink2, mb: 1 }}
                  >
                    <time dateTime={entry.date}>
                      {formatDate(entry.date, { dateStyle: "long" })}
                    </time>
                  </Typography>
                  {CHANGELOG_CATEGORIES.map((category) => {
                    const section = entry.sections.find(
                      (s) => s.category === category,
                    );
                    if (!section || section.items.length === 0) {
                      return null;
                    }
                    return (
                      <Box key={category} sx={{ mt: 1.5 }}>
                        <Typography
                          variant="subtitle2"
                          component="h4"
                          sx={{
                            color: tallyTokens.ink2,
                            textTransform: "uppercase",
                            letterSpacing: "0.08em",
                          }}
                        >
                          {categoryLabels[category]}
                        </Typography>
                        <Box
                          component="ul"
                          sx={{
                            m: 0,
                            mt: 0.5,
                            pl: 3,
                            listStylePosition: "outside",
                          }}
                        >
                          {section.items.map((item, index) => (
                            <Typography
                              key={index}
                              component="li"
                              variant="body1"
                              sx={{
                                color: tallyTokens.ink2,
                                lineHeight: 1.7,
                                overflowWrap: "anywhere",
                              }}
                            >
                              {item}
                            </Typography>
                          ))}
                        </Box>
                      </Box>
                    );
                  })}
                </Paper>
              ))}
            </Box>
          ))
        )}
      </Container>
    </Box>
  );
};

/**
 * Owns the page's language (default Danish, remembered in localStorage) with a
 * page-local Lingui instance, so switching here never changes the app-wide
 * locale that the rest of Tally follows from the browser.
 */
const Changelog: React.FC<ChangelogProps> = ({
  entries = CHANGELOG_ENTRIES,
  translations = CHANGELOG_ENTRIES_DA,
}) => {
  const i18n = useMemo<I18n>(() => setupI18n(), []);
  // Null until the first catalog has loaded, so nothing flashes in English.
  const [language, setLanguage] = useState<ChangelogLanguage | null>(null);
  const latestRequest = useRef(0);

  const selectLanguage = useCallback(
    async (next: ChangelogLanguage) => {
      const request = ++latestRequest.current;
      const messages = await loadMessages(next);
      // A newer selection superseded this one while its catalog was loading.
      if (request !== latestRequest.current) {
        return;
      }
      i18n.load(next, messages);
      i18n.activate(next);
      setLanguage(next);
    },
    [i18n],
  );

  useEffect(() => {
    void selectLanguage(readStoredLanguage());
  }, [selectLanguage]);

  if (!language) {
    return null;
  }

  return (
    <I18nProvider i18n={i18n}>
      <ChangelogPage
        entries={entries}
        translations={translations}
        language={language}
        onLanguageChange={(next) => {
          storeLanguage(next);
          void selectLanguage(next);
        }}
      />
    </I18nProvider>
  );
};

export default Changelog;
